import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabaseUrl, getSupabaseAnonKey } from '../lib/environments';
import { formatPhoneForInput } from '../utils/phoneFormatting';
import { JoinCircleForm } from './JoinCircleForm';
import { JoinCircleSuccess } from './JoinCircleSuccess';

type Step = 'loading' | 'form' | 'success' | 'error';

export const JoinCircle: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  const [circleName, setCircleName] = useState('');
  const [inviterName, setInviterName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // 'email' for QR templates and email-direct invites; 'phone' for phone-
  // direct invites. Drives which input the form renders + which field the
  // accept POST sends.
  const [identifierMode, setIdentifierMode] = useState<'email' | 'phone'>('email');
  const [submitting, setSubmitting] = useState(false);
  const [hasPromo, setHasPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [circleEventId, setCircleEventId] = useState<string | null>(null);
  const [circleId, setCircleId] = useState<string | null>(null);
  const [isDirectInvite, setIsDirectInvite] = useState(false);

  useEffect(() => {
    loadInvite();
  }, [token]);

  const loadInvite = async () => {
    if (!token) {
      setError('Invalid invitation link');
      setStep('error');
      return;
    }

    try {
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/qr-circle-invite-api?token=${token}`,
        { headers: { 'Authorization': `Bearer ${getSupabaseAnonKey()}` } }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error || 'This invitation link is invalid or has expired.');
        setStep('error');
        return;
      }

      setCircleName(result.circle_name);
      setInviterName(result.inviter_name);
      if (result.circle_id) setCircleId(result.circle_id);
      if (result.circle_event_id) setCircleEventId(result.circle_event_id);
      if (result.is_direct_invite) setIsDirectInvite(true);
      // Prefill name + identifier from a direct invite so the recipient
      // doesn't have to retype what the inviter already provided.
      if (result.invited_display_name) setFullName(result.invited_display_name);
      if (result.invited_phone) {
        // Pretty-print the prefilled number so the recipient sees
        // (442) 494-8170 instead of the raw stored digits.
        setPhone(formatPhoneForInput(result.invited_phone, '+1'));
        setIdentifierMode('phone');
      } else if (result.invited_email) {
        setEmail(result.invited_email);
        setIdentifierMode('email');
      }
      if (result.circle_event_id) {
        // Circle has an event — send them to the event page. Preserve the
        // QR token via ?invite= so the event page can auto-accept the
        // logged-in user (or pass the token through "Already have an
        // account?" → /login → back to /events/X?invite=). Without this
        // the QR context was lost on redirect and the join silently fell
        // back to a non-QR public-self-join, which left guests stuck in
        // pending status when they signed in via phone OTP afterward.
        const qs = token ? `?invite=${encodeURIComponent(token)}` : '';
        navigate(`/events/${result.circle_event_id}${qs}`);
        return;
      }
      setStep('form');
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation');
      setStep('error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const identifier = identifierMode === 'phone' ? phone.trim() : email.trim();
    if (!fullName.trim() || !identifier) return;

    setSubmitting(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {
        qr_token: token,
        full_name: fullName.trim(),
        joined_via: 'link',
      };
      if (identifierMode === 'phone') body.phone = identifier;
      else body.email = identifier.toLowerCase();

      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/qr-circle-invite-api?action=accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getSupabaseAnonKey()}`,
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to join circle');
        setSubmitting(false);
        return;
      }

      setCircleName(data.circle_name || circleName);
      setHasPromo(data.has_promo || false);
      setPromoMessage(data.promo_message || null);
      if (data.circle_id) setCircleId(data.circle_id);

      // If this circle has an event, redirect to the event landing page
      const eventId = data.circle_event_id || circleEventId;
      if (eventId) {
        navigate(`/events/${eventId}`);
        return;
      }

      setStep('success');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-8 text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <img src="/kizu-mascot.png" alt="Kizu" className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Unable to Load Invitation</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">Please ask the person who invited you for a new link.</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center p-4">
        <JoinCircleSuccess
          circleName={circleName}
          hasPromo={hasPromo}
          promoMessage={promoMessage}
          circleId={circleId}
          inviteToken={token || null}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-4">
      <JoinCircleForm
        fullName={fullName}
        email={email}
        phone={phone}
        identifierMode={identifierMode}
        identifierLocked={isDirectInvite}
        submitting={submitting}
        error={error}
        circleName={circleName}
        inviterName={inviterName}
        onFullNameChange={setFullName}
        onEmailChange={setEmail}
        onPhoneChange={setPhone}
        onIdentifierModeChange={setIdentifierMode}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
